import React from 'react';
import './TermsOfService.css';

const TermsOfService = () => {
  return (
    <div className="tos-container">
      <div className="tos-header">
        <div className="tos-header-content">
          <h1>Terms of Service</h1>
          <p className="last-updated">Last updated: February 28, 2026</p>
        </div>
      </div>

      <div className="tos-content">
        <div className="tos-nav">
          <h3>Contents</h3>
          <ul>
            <li><a href="#acceptance">1. Acceptance of Terms</a></li>
            <li><a href="#description">2. Service Description</a></li>
            <li><a href="#eligibility">3. Eligibility</a></li>
            <li><a href="#account">4. Account Registration</a></li>
            <li><a href="#usage">5. Acceptable Use</a></li>
            <li><a href="#privacy">6. Privacy & Data</a></li>
            <li><a href="#payment">7. Payment Terms</a></li>
            <li><a href="#intellectual">8. Intellectual Property</a></li>
            <li><a href="#disclaimers">9. Disclaimers</a></li>
            <li><a href="#limitation">10. Limitation of Liability</a></li>
            <li><a href="#termination">11. Termination</a></li>
            <li><a href="#changes">12. Changes to Terms</a></li>
            <li><a href="#contact">13. Contact Information</a></li>
          </ul>
        </div>

        <div className="tos-main">
          <section id="acceptance">
            <h2>1. Acceptance of Terms</h2>
            <p>By accessing or using our Prompttar platform ("Service"), including our web application and related tools, you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Service.</p>
          </section>

          <section id="description">
            <h2>2. Service Description</h2>
            <p>Our Service provides AI-powered prompt engineering education and coding tools designed for developers and learners. The Service includes:</p>
            <ul>
              <li>Interactive coding challenges and exercises</li>
              <li>AI-driven prompt feedback and optimization</li>
              <li>Real-time coding guidance and debugging support</li>
              <li>Access to modern coding patterns and trending techniques</li>
              <li>Progress tracking and skill development analytics</li>
            </ul>
            <p>We aim to deliver fast, intuitive, and effective learning solutions to help you master prompt engineering and improve your coding skills.</p>
          </section>

          <section id="eligibility">
            <h2>3. Eligibility</h2>
            <p>You must be at least 18 years old and have the legal capacity to enter into this agreement. By using our Service, you represent and warrant that you meet these requirements.</p>
          </section>

          <section id="account">
            <h2>4. Account Registration and Security</h2>
            <p>To access certain features of our Service, you must create an account. You agree to:</p>
            <ul>
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Notify us immediately of any unauthorized use</li>
              <li>Accept responsibility for all activities under your account</li>
            </ul>
          </section>

          <section id="usage">
            <h2>5. Acceptable Use Policy</h2>
            <p>You agree not to use our Service to:</p>
            <ul>
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe upon intellectual property rights</li>
              <li>Submit harmful, offensive, or inappropriate prompts or content</li>
              <li>Attempt to reverse engineer or hack our systems</li>
              <li>Use automated tools to excessively access our Service</li>
              <li>Share confidential prompts or solutions without authorization</li>
            </ul>
          </section>

          <section id="privacy">
            <h2>6. Privacy and Data Protection</h2>
            <p>Your privacy is important to us. Our collection and use of your data is governed by our Privacy Policy, which is incorporated into these Terms by reference. By using our Service, you consent to our data practices as described in the Privacy Policy.</p>
            <p>We implement appropriate security measures to protect your data, but cannot guarantee absolute security of information transmitted over the internet.</p>
          </section>

          <section id="payment">
            <h2>7. Payment Terms</h2>
            <p>Certain features of our Service may require payment. By purchasing a subscription or premium features:</p>
            <ul>
              <li>You agree to pay all applicable fees</li>
              <li>Payments are processed through secure third-party providers</li>
              <li>Subscriptions automatically renew unless cancelled</li>
              <li>Refunds are subject to our refund policy</li>
              <li>We reserve the right to change pricing with notice</li>
            </ul>
          </section>

          <section id="intellectual">
            <h2>8. Intellectual Property Rights</h2>
            <p>The Service, including all content, features, and functionality, is owned by us and protected by international copyright, trademark, and other intellectual property laws. You are granted a limited, non-exclusive, non-transferable license to use the Service for your personal or educational purposes.</p>
            <p>You retain ownership of your prompts and challenge submissions, but grant us permission to process this data to provide our learning and feedback services.</p>
          </section>

          <section id="disclaimers">
            <h2>9. Disclaimers</h2>
            <p>THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:</p>
            <ul>
              <li>Merchantability and fitness for a particular purpose</li>
              <li>Accuracy or effectiveness of AI-driven feedback</li>
              <li>Guaranteed improvement in coding or prompt engineering skills</li>
              <li>Uninterrupted or error-free service operation</li>
            </ul>
            <p>Learning outcomes may vary and are influenced by many factors beyond our control, including individual effort and prior experience.</p>
          </section>

          <section id="limitation">
            <h2>10. Limitation of Liability</h2>
            <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR EDUCATIONAL OPPORTUNITIES.</p>
            <p>Our total liability to you shall not exceed the amount you paid us in the twelve months preceding the claim.</p>
          </section>

          <section id="termination">
            <h2>11. Termination</h2>
            <p>Either party may terminate this agreement at any time. We may suspend or terminate your access to the Service for violation of these Terms or other legitimate business reasons. Upon termination:</p>
            <ul>
              <li>Your right to use the Service immediately ceases</li>
              <li>We may delete your account and associated data</li>
              <li>Provisions regarding intellectual property and liability survive</li>
            </ul>
          </section>

          <section id="changes">
            <h2>12. Changes to Terms</h2>
            <p>We reserve the right to modify these Terms at any time. We will notify you of material changes by email or through the Service. Your continued use after such notification constitutes acceptance of the modified Terms.</p>
          </section>

          <section id="contact">
            <h2>13. Contact Information</h2>
            <p>If you have questions about these Terms, please contact us at:</p>
            <div className="contact-info">
              <p>Email: legal@prompttar.com</p>
              <p>Address: Prompttar Legal Department, [Your Business Address]</p>
            </div>
          </section>

          <div className="tos-footer">
            <p>These Terms of Service constitute the entire agreement between you and Prompttar regarding the use of our Service.</p>
            <p className="governing-law">These Terms are governed by the laws of [Your Jurisdiction] without regard to conflict of law principles.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;